import { useSyncExternalStore } from "react";
import type { BrokerFigure, FigureKind } from "@helm/core";

/**
 * The only part of the firm-rules feature (ADR-0047) in the entry chunk (audit S-12 budget). The
 * owner's firm-rules store, HELM's daily history and the "HELM-calculated from your firm rules"
 * figure math are a separate chunk (`firmRuntime.ts`), loaded once an account is connected (the
 * view model asks, viewmodel/account.ts) or when Settings › Account information opens.
 *
 * Until it is in, every account's risk figures are the Rithmic ones (tables/brokerRisk.ts, in the
 * entry chunk); the HELM-calculated second source is "loading" (`FIRM_RULES_LOADING`, "…" in the
 * risk strip), never a number and never zero. A chunk that fails to load leaves the HELM figures
 * unavailable with the reason and a Retry in the account details; trading is not affected.
 */

type Runtime = typeof import("./firmRuntime");

export type FirmRuntimeState =
  | { status: "idle" | "loading"; runtime: null }
  /** `retried`: a Retry failed too (a browser may keep a failed module download: Reload app). */
  | { status: "failed"; runtime: null; retried: boolean }
  | { status: "ready"; runtime: Runtime };

/** The HELM-calculated figures' reason while the rules (and the code computing them) load. */
export const FIRM_RULES_LOADING = "Loading your firm rules";
/** Their reason when the firm-rules chunk could not be loaded. */
export const FIRM_RUNTIME_FAILED =
  "TradePrizm's firm-rule figures could not be loaded (Retry in the account details; trading is not affected)";

/** ⚙ Reload spacing of HELM's daily history while the trading day does not change. */
export const DAY_HISTORY_REFRESH_MS = 5 * 60_000;

let state: FirmRuntimeState = { status: "idle", runtime: null };
const listeners = new Set<() => void>();

function emit(): void {
  for (const l of listeners) l();
}

function setState(next: FirmRuntimeState): void {
  state = next;
  emit();
}

/** Load the firm-rules chunk (idempotent; after a failure only `retry` loads again). */
export function loadFirmRuntime(retry = false): void {
  if (state.status === "loading" || state.status === "ready") return;
  if (state.status === "failed" && !retry) return;
  const retried = state.status === "failed";
  setState({ status: "loading", runtime: null });
  import("./firmRuntime").then(
    (runtime) => {
      // The rules and the history report their own progress through the same listeners.
      runtime.onFirmRulesChange(emit);
      runtime.onDayHistoryChange(emit);
      setState({ status: "ready", runtime });
    },
    (e: unknown) => {
      console.error("[lazy] firm rules failed", e);
      setState({ status: "failed", runtime: null, retried });
    },
  );
}

/** The loaded chunk, or null while it is not (yet) in. */
export function firmRuntime(): Runtime | null {
  return state.runtime;
}

export function firmRuntimeStatus(): FirmRuntimeState["status"] {
  return state.status;
}

/** Load status changes, and once loaded every firm-rules / daily-history change. */
export function onFirmChange(l: () => void): () => void {
  listeners.add(l);
  return () => {
    listeners.delete(l);
  };
}

export function useFirmRuntime(): FirmRuntimeState {
  return useSyncExternalStore(onFirmChange, () => state);
}

/**
 * The HELM-calculated second source of one room figure while HELM's figures are not computed:
 * unavailable with the reason (loading, or the chunk failed), so `chooseRoom` keeps the Rithmic
 * figure and notes why the HELM one is missing.
 */
export function pendingHelmFigure(kind: FigureKind, failed: boolean): BrokerFigure {
  return {
    kind,
    source: "unavailable",
    valueCents: null,
    verification: null,
    label: "Unavailable",
    reason: failed ? FIRM_RUNTIME_FAILED : FIRM_RULES_LOADING,
    formula: null,
    candidates: [],
    inputs: [],
  };
}

/** True when a shown figure has no value only because HELM's own figure is still loading. */
export function helmFigurePending(f: BrokerFigure): boolean {
  return f.valueCents === null && f.alternate?.reason === FIRM_RULES_LOADING;
}

// ---- synced-document notices -------------------------------------------------------------------

let docChanged: ((accountId: string, version: number) => void) | null = null;

/** Called once by firmRules.ts when it evaluates (whichever chunk brought it in). */
export function firmRulesStoreLoaded(handler: (accountId: string, version: number) => void): void {
  docChanged = handler;
}

/** Another device saved an account's firm rules (bus `DocChanged`, session.ts). */
export function firmRulesDocChanged(accountId: string, version: number): void {
  // Not loaded: nothing is cached, so the first load reads the new version anyway.
  docChanged?.(accountId, version);
}
